import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase";
import PageHeader from "../../components/PageHeader";
import Modal from "../../components/Modal";
import Toggle from "../../components/Toggle";
import { Banner, useToast } from "../../components/Feedback";
import QuizDetail from "./QuizDetail";
import type { Question, Suggestion } from "./quizTypes";
import {
  MIN_ANSWERS,
  allForms,
  normalize,
  normalizeAnswerLine,
  parseImport,
  relativeTime,
  type ExistingQuestion,
  type ParsedQuestion,
} from "./quizText";

type StatusFilter =
  | "all"
  | "live"
  | "draft"
  | "incomplete"
  | "suggested"
  | "duplicate";

type SortKey =
  | "answersAsc"
  | "answersDesc"
  | "suggestions"
  | "updated"
  | "newest"
  | "alpha";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "answersAsc", label: "Cevabı az → çok" },
  { key: "answersDesc", label: "Cevabı çok → az" },
  { key: "suggestions", label: "Önerisi çok olan" },
  { key: "updated", label: "Son güncellenen" },
  { key: "newest", label: "Son eklenen" },
  { key: "alpha", label: "A → Z" },
];

const FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Tümü" },
  { key: "live", label: "Yayında" },
  { key: "draft", label: "Taslak" },
  { key: "incomplete", label: "Cevabı eksik" },
  { key: "suggested", label: "Önerisi var" },
  { key: "duplicate", label: "Mükerrer" },
];

const PAGE_SIZES = [25, 50, 100, 0]; // 0 = tümü

/** rejectedAnswers kayıtlarında soru bankasını işaretleyen önek. */
const QUIZ_PREFIX = "quiz:";

export default function Quiz() {
  const [rows, setRows] = useState<Question[] | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("answersAsc");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Diyaloglar
  const [newOpen, setNewOpen] = useState(false);
  const [newText, setNewText] = useState("");
  const [newAnswers, setNewAnswers] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<Question | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagValue, setTagValue] = useState("");

  const { show, node: toast } = useToast();

  const load = async () => {
    setBusy("load");
    setErr(null);
    try {
      // Öneriler soruların yanında geliyor: liste her satırda "kaç öneri
      // bekliyor" gösterebilsin diye. Tek seferlik iki okuma, soru başına
      // sorgu atmaktan çok daha ucuz.
      const [qSnap, rSnap] = await Promise.all([
        getDocs(collection(db, "quizQuestions")),
        getDocs(collection(db, "rejectedAnswers")),
      ]);
      setRows(
        qSnap.docs.map((d) => {
          const x = d.data();
          return {
            id: d.id,
            text: (x.text as string) ?? "",
            answers: ((x.answers as string[]) ?? []).slice(),
            tags: ((x.tags as string[]) ?? []).slice(),
            enabled: (x.enabled as boolean) ?? true,
            createdAtMs: x.createdAt?.toMillis?.(),
            updatedAtMs: x.updatedAt?.toMillis?.(),
          };
        })
      );
      setSuggestions(
        rSnap.docs
          .filter((d) =>
            ((d.data().categoryKey as string) ?? "").startsWith(QUIZ_PREFIX)
          )
          .map((d) => {
            const x = d.data();
            return {
              id: d.id,
              word: (x.word as string) ?? "",
              questionId: (x.categoryKey as string).slice(QUIZ_PREFIX.length),
              count: Number(x.count ?? 1),
              lastAtMs: x.lastAt?.toMillis?.(),
            };
          })
          .sort((a, b) => b.count - a.count)
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const byQuestion = useMemo(() => {
    const m = new Map<string, Suggestion[]>();
    for (const s of suggestions) {
      const list = m.get(s.questionId);
      if (list) list.push(s);
      else m.set(s.questionId, [s]);
    }
    return m;
  }, [suggestions]);

  /** Aynı metinle iki kez girilmiş sorular — oyunda tekrar hissi yaratıyor. */
  const duplicateIds = useMemo(() => {
    const seen = new Map<string, string[]>();
    (rows ?? []).forEach((r) => {
      const k = normalize(r.text);
      const list = seen.get(k);
      if (list) list.push(r.id);
      else seen.set(k, [r.id]);
    });
    const dup = new Set<string>();
    seen.forEach((ids) => {
      if (ids.length > 1) ids.forEach((id) => dup.add(id));
    });
    return dup;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = normalize(search);
    let list = rows.filter((r) => {
      if (q && !normalize(r.text).includes(q) && !r.answers.some((a) => a.includes(q)))
        return false;
      const n = r.answers.length;
      switch (status) {
        case "live":
          return r.enabled && n >= MIN_ANSWERS;
        case "draft":
          return !r.enabled;
        case "incomplete":
          return n < MIN_ANSWERS;
        case "suggested":
          return (byQuestion.get(r.id)?.length ?? 0) > 0;
        case "duplicate":
          return duplicateIds.has(r.id);
        default:
          return true;
      }
    });

    const sug = (r: Question) => byQuestion.get(r.id)?.length ?? 0;
    list = list.slice().sort((a, b) => {
      switch (sort) {
        case "answersDesc":
          return b.answers.length - a.answers.length;
        case "suggestions":
          return sug(b) - sug(a) || a.answers.length - b.answers.length;
        case "updated":
          return (b.updatedAtMs ?? 0) - (a.updatedAtMs ?? 0);
        case "newest":
          return (b.createdAtMs ?? 0) - (a.createdAtMs ?? 0);
        case "alpha":
          return a.text.localeCompare(b.text, "tr");
        default:
          return a.answers.length - b.answers.length;
      }
    });
    return list;
  }, [rows, search, status, sort, byQuestion, duplicateIds]);

  // Filtre değişince eski sayfada takılı kalmayalım.
  useEffect(() => {
    setPage(0);
  }, [search, status, sort, pageSize]);

  const pageCount = pageSize === 0 ? 1 : Math.ceil(filtered.length / pageSize);
  const visible =
    pageSize === 0
      ? filtered
      : filtered.slice(page * pageSize, page * pageSize + pageSize);

  const current = rows?.find((r) => r.id === openId) ?? null;

  // ——— Yazma işlemleri ———

  const save = async (id: string, patch: Partial<Question>) => {
    setBusy("save");
    setErr(null);
    try {
      const base = rows?.find((r) => r.id === id);
      if (!base) return;
      const next = { ...base, ...patch };
      await updateDoc(doc(db, "quizQuestions", id), {
        text: next.text,
        answers: next.answers,
        tags: next.tags,
        enabled: next.enabled,
        answerCount: next.answers.length,
        updatedAt: serverTimestamp(),
      });
      setRows(
        (prev) =>
          prev?.map((r) =>
            r.id === id ? { ...next, updatedAtMs: Date.now() } : r
          ) ?? null
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  /** Listeden, detaya girmeden aç/kapa. */
  const toggleEnabled = async (q: Question, next: boolean) => {
    if (next && q.answers.length < MIN_ANSWERS) return;
    await save(q.id, { enabled: next });
    show(next ? "Soru yayına alındı." : "Soru yayından kaldırıldı.");
  };

  const createQuestion = async () => {
    const text = newText.trim();
    if (!text) return;
    setBusy("new");
    setErr(null);
    try {
      const answers = Array.from(
        new Set(
          newAnswers.split("\n").map(normalizeAnswerLine).filter(Boolean)
        )
      );
      const ref = await addDoc(collection(db, "quizQuestions"), {
        text,
        answers,
        tags: [],
        // Yeterli cevabı varsa doğrudan yayına girsin; yoksa taslak kalsın.
        enabled: answers.length >= MIN_ANSWERS,
        answerCount: answers.length,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setRows((prev) => [
        {
          id: ref.id,
          text,
          answers,
          tags: [],
          enabled: answers.length >= MIN_ANSWERS,
          createdAtMs: Date.now(),
          updatedAtMs: Date.now(),
        },
        ...(prev ?? []),
      ]);
      setNewOpen(false);
      setNewText("");
      setNewAnswers("");
      setOpenId(ref.id);
      show(
        answers.length >= MIN_ANSWERS
          ? "Soru eklendi ve yayına alındı."
          : `Soru eklendi. Yayına girmesi için ${
              MIN_ANSWERS - answers.length
            } cevap daha gerekiyor.`
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const removeQuestion = async (q: Question) => {
    setBusy("del");
    try {
      await deleteDoc(doc(db, "quizQuestions", q.id));
      setRows((prev) => prev?.filter((r) => r.id !== q.id) ?? null);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(q.id);
        return n;
      });
      if (openId === q.id) setOpenId(null);
      setConfirmDelete(null);
      show("Soru silindi.");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // ——— Toplu işlemler ———

  const selectedRows = useMemo(
    () => (rows ?? []).filter((r) => selected.has(r.id)),
    [rows, selected]
  );

  /**
   * Firestore toplu yazımı 500 işlemde sınırlı; parçalara bölüyoruz.
   * `apply` her belge için batch'e ne yazılacağını söylüyor.
   */
  const runBatched = async (
    items: Question[],
    apply: (batch: ReturnType<typeof writeBatch>, q: Question) => void
  ) => {
    let batch = writeBatch(db);
    let n = 0;
    for (const q of items) {
      apply(batch, q);
      if (++n % 400 === 0) {
        await batch.commit();
        batch = writeBatch(db);
      }
    }
    if (n % 400 !== 0) await batch.commit();
  };

  const bulkEnable = async (next: boolean) => {
    // Cevabı yetmeyen soru yayına alınamaz — sessizce atlanıyor, sonuçta
    // kaç tanesinin atlandığı bildiriliyor.
    const targets = next
      ? selectedRows.filter((r) => r.answers.length >= MIN_ANSWERS)
      : selectedRows;
    const skipped = selectedRows.length - targets.length;
    if (!targets.length) {
      show(`Hiçbiri yayına alınamadı: ${MIN_ANSWERS} cevap şartı.`, "warn");
      return;
    }
    setBusy("bulk");
    try {
      await runBatched(targets, (batch, q) =>
        batch.update(doc(db, "quizQuestions", q.id), {
          enabled: next,
          updatedAt: serverTimestamp(),
        })
      );
      const ids = new Set(targets.map((t) => t.id));
      setRows(
        (prev) =>
          prev?.map((r) => (ids.has(r.id) ? { ...r, enabled: next } : r)) ?? null
      );
      setSelected(new Set());
      show(
        `${targets.length} soru ${next ? "yayına alındı" : "yayından kaldırıldı"}.` +
          (skipped ? ` ${skipped} tanesi cevabı eksik olduğu için atlandı.` : "")
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const bulkDelete = async () => {
    setBusy("bulk");
    try {
      await runBatched(selectedRows, (batch, q) =>
        batch.delete(doc(db, "quizQuestions", q.id))
      );
      const ids = new Set(selectedRows.map((t) => t.id));
      setRows((prev) => prev?.filter((r) => !ids.has(r.id)) ?? null);
      setSelected(new Set());
      setConfirmBulkDelete(false);
      show(`${ids.size} soru silindi.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const bulkTag = async () => {
    const tag = normalize(tagValue);
    if (!tag) return;
    setBusy("bulk");
    try {
      const targets = selectedRows.filter((r) => !r.tags.includes(tag));
      await runBatched(targets, (batch, q) =>
        batch.update(doc(db, "quizQuestions", q.id), {
          tags: [...q.tags, tag],
          updatedAt: serverTimestamp(),
        })
      );
      const ids = new Set(targets.map((t) => t.id));
      setRows(
        (prev) =>
          prev?.map((r) =>
            ids.has(r.id) ? { ...r, tags: [...r.tags, tag] } : r
          ) ?? null
      );
      setTagOpen(false);
      setTagValue("");
      setSelected(new Set());
      show(`${targets.length} soruya "${tag}" etiketi eklendi.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // ——— Öneriler ———

  const acceptSuggestion = async (s: Suggestion) => {
    const q = rows?.find((r) => r.id === s.questionId);
    if (!q) return;
    setBusy("sug");
    try {
      const w = normalize(s.word);
      if (!allForms(q.answers).has(w)) {
        await save(q.id, { answers: [...q.answers, w] });
      }
      await deleteDoc(doc(db, "rejectedAnswers", s.id));
      setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
      show(`"${s.word}" cevaplara eklendi.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const dismissSuggestion = async (s: Suggestion) => {
    setBusy("sug");
    try {
      await deleteDoc(doc(db, "rejectedAnswers", s.id));
      setSuggestions((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // ——— İçe aktarma ———

  const existingByText = useMemo(() => {
    const m = new Map<string, ExistingQuestion>();
    (rows ?? []).forEach((r) =>
      m.set(normalize(r.text), { id: r.id, answers: r.answers })
    );
    return m;
  }, [rows]);

  const preview = useMemo<ParsedQuestion[]>(
    () =>
      importOpen ? parseImport(importText, { existing: existingByText }) : [],
    [importOpen, importText, existingByText]
  );
  const importable = preview.filter(
    (p) => p.errors.length === 0 && p.mode !== "skip"
  );
  const creates = importable.filter((p) => p.mode === "create");
  const merges = importable.filter((p) => p.mode === "merge");
  const mergedAnswers = merges.reduce((s, p) => s + p.newAnswers.length, 0);

  const runImport = async () => {
    if (!importable.length) return;
    setBusy("import");
    setErr(null);
    try {
      let batch = writeBatch(db);
      let n = 0;
      const flush = async () => {
        if (++n % 400 === 0) {
          await batch.commit();
          batch = writeBatch(db);
        }
      };

      for (const q of creates) {
        const ref = doc(collection(db, "quizQuestions"));
        batch.set(ref, {
          text: q.text,
          answers: q.answers,
          tags: [],
          enabled: q.answers.length >= MIN_ANSWERS,
          answerCount: q.answers.length,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        await flush();
      }

      // Mevcut soruya yalnızca cevap ekliyoruz. `enabled` ve `tags`'e
      // dokunmuyoruz: ikisi de panelden elle verilmiş kararlar.
      for (const q of merges) {
        const before = existingByText.get(normalize(q.text));
        if (!before || !q.mergeId) continue;
        const answers = [...before.answers, ...q.newAnswers];
        batch.update(doc(db, "quizQuestions", q.mergeId), {
          answers,
          answerCount: answers.length,
          updatedAt: serverTimestamp(),
        });
        await flush();
      }
      if (n % 400 !== 0) await batch.commit();

      const skipped = preview.length - importable.length;
      setImportText("");
      setImportOpen(false);
      show(
        [
          creates.length ? `${creates.length} soru eklendi.` : "",
          merges.length
            ? `${merges.length} soruya ${mergedAnswers} cevap eklendi.`
            : "",
          skipped ? `${skipped} blok atlandı.` : "",
        ]
          .filter(Boolean)
          .join(" ")
      );
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Hazır paketi içe aktarma kutusuna yükler.
   *
   * Doğrudan yazmak yerine önizlemeden geçiriyoruz: paket zamanla bankayla
   * örtüşüyor ve hangi soruların atlanacağını görmeden aktarmak "ikinci kez
   * bastım, kopya çıktı mı?" sorusunu doğuruyordu. JSON yerine blok metnine
   * çeviriyoruz — hem küçük hem de aktarmadan önce elle düzeltilebilir.
   */
  const loadSeed = async () => {
    setBusy("seed");
    setErr(null);
    try {
      const res = await fetch("/quiz-seed.json");
      if (!res.ok) throw new Error("quiz-seed.json okunamadı");
      const payload = (await res.json()) as {
        questions?: { text: string; answers: string[] }[];
      };
      const blocks = (payload.questions ?? [])
        .map((q) => [q.text, ...q.answers].join("\n"))
        .join("\n\n");
      if (!blocks) throw new Error("Pakette soru yok");
      setImportText(blocks);
      setImportOpen(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // ——— Sayılar ———

  const stats = useMemo(() => {
    const list = rows ?? [];
    return {
      total: list.length,
      live: list.filter((r) => r.enabled && r.answers.length >= MIN_ANSWERS)
        .length,
      draft: list.filter((r) => !r.enabled).length,
      incomplete: list.filter((r) => r.answers.length < MIN_ANSWERS).length,
      answers: list.reduce((s, r) => s + r.answers.length, 0),
      suggestions: suggestions.length,
    };
  }, [rows, suggestions]);

  const allOnPageSelected =
    visible.length > 0 && visible.every((r) => selected.has(r.id));

  const toggleAllOnPage = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allOnPageSelected) visible.forEach((r) => n.delete(r.id));
      else visible.forEach((r) => n.add(r.id));
      return n;
    });
  };

  return (
    <div className="pb-28">
      <PageHeader
        title="Soru Bankası"
        subtitle={`Her sorunun kendi cevapları var. En az ${MIN_ANSWERS} cevabı olan sorular oyuna giriyor.`}
        actions={
          <>
            <button
              className="btn-ghost text-sm"
              disabled={busy !== null}
              onClick={load}
              title="Listeyi yenile"
            >
              {busy === "load" ? "…" : "↻ Yenile"}
            </button>
            <button
              className="btn-outline text-sm"
              disabled={busy !== null}
              onClick={() => setImportOpen(true)}
            >
              Toplu ekle
            </button>
            <button
              className="btn-primary text-sm"
              disabled={busy !== null}
              onClick={() => setNewOpen(true)}
            >
              + Yeni soru
            </button>
          </>
        }
      />

      {err && (
        <Banner tone="error" onClose={() => setErr(null)}>
          {err}
        </Banner>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        <Metric value={stats.total} label="soru" />
        <Metric value={stats.live} label="yayında" tone="good" />
        <Metric value={stats.draft} label="taslak" />
        <Metric
          value={stats.incomplete}
          label="cevabı eksik"
          tone={stats.incomplete ? "warn" : "neutral"}
        />
        <Metric value={stats.answers.toLocaleString("tr-TR")} label="cevap" />
        <Metric
          value={stats.suggestions}
          label="bekleyen öneri"
          tone={stats.suggestions ? "warn" : "neutral"}
        />
      </div>

      {/* Araç çubuğu */}
      <div className="card p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input py-2 text-sm flex-1 min-w-[200px]"
            placeholder="Soru veya cevap ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="input py-2 text-sm w-auto"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className="input py-2 text-sm w-auto"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? "Tümü" : `${n} satır`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              className={[
                "px-3 py-1.5 rounded-lg text-xs font-bold transition",
                status === f.key
                  ? "bg-primary text-white shadow"
                  : "text-textMuted hover:text-textPrimary hover:bg-surfaceHigh",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Toplu işlem çubuğu */}
      {selected.size > 0 && (
        <div className="card p-3 mb-4 flex flex-wrap items-center gap-2 border-primary/40">
          <span className="text-sm font-bold px-1">
            {selected.size} soru seçildi
          </span>
          <button
            className="btn-ghost text-xs"
            disabled={busy !== null}
            onClick={() => bulkEnable(true)}
          >
            Yayına al
          </button>
          <button
            className="btn-ghost text-xs"
            disabled={busy !== null}
            onClick={() => bulkEnable(false)}
          >
            Yayından kaldır
          </button>
          <button
            className="btn-ghost text-xs"
            disabled={busy !== null}
            onClick={() => setTagOpen(true)}
          >
            Etiketle
          </button>
          <button
            className="btn-ghost text-xs text-danger"
            disabled={busy !== null}
            onClick={() => setConfirmBulkDelete(true)}
          >
            Sil
          </button>
          <button
            className="btn-ghost text-xs ml-auto"
            onClick={() => setSelected(new Set())}
          >
            Seçimi temizle
          </button>
        </div>
      )}

      {/* Liste */}
      {rows === null ? (
        <div className="card p-10 text-center text-textMuted">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="text-4xl mb-3">🧠</div>
          <p className="text-textSecondary text-sm mb-5 max-w-sm mx-auto">
            Soru bankası boş. Hazır paketi önizleyip aktarabilir ya da elle
            soru ekleyebilirsin.
          </p>
          <div className="flex justify-center gap-2">
            <button
              className="btn-primary"
              disabled={busy !== null}
              onClick={loadSeed}
            >
              {busy === "seed" ? "Yükleniyor…" : "Hazır paketi getir"}
            </button>
            <button className="btn-outline" onClick={() => setNewOpen(true)}>
              Elle ekle
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-stroke bg-surfaceHigh/40 text-[11px] font-extrabold uppercase tracking-wider text-textMuted">
              <input
                type="checkbox"
                className="accent-primary"
                checked={allOnPageSelected}
                onChange={toggleAllOnPage}
                title="Sayfadakilerin tümünü seç"
              />
              <span className="flex-1">Soru</span>
              <span className="w-24 text-center hidden sm:block">Cevap</span>
              <span className="w-20 text-center hidden lg:block">Öneri</span>
              <span className="w-28 text-right hidden lg:block">Güncelleme</span>
              <span className="w-16 text-center">Yayın</span>
            </div>

            <div className="divide-y divide-stroke/70">
              {visible.map((q) => {
                const sug = byQuestion.get(q.id)?.length ?? 0;
                const playable = q.answers.length >= MIN_ANSWERS;
                const pct = Math.min(
                  100,
                  Math.round((q.answers.length / MIN_ANSWERS) * 100)
                );
                return (
                  <div
                    key={q.id}
                    className={[
                      "flex items-center gap-3 px-4 py-3 transition cursor-pointer",
                      selected.has(q.id)
                        ? "bg-primary/10"
                        : "hover:bg-surfaceHigh/50",
                    ].join(" ")}
                    onClick={() => setOpenId(q.id)}
                  >
                    <input
                      type="checkbox"
                      className="accent-primary shrink-0"
                      checked={selected.has(q.id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() =>
                        setSelected((prev) => {
                          const n = new Set(prev);
                          if (n.has(q.id)) n.delete(q.id);
                          else n.add(q.id);
                          return n;
                        })
                      }
                    />

                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm leading-snug truncate">
                        {q.text || <i className="text-textMuted">(başlıksız)</i>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {!playable && (
                          <span className="pill bg-warning/15 text-warning text-[10px] px-2 py-0.5">
                            {MIN_ANSWERS - q.answers.length} cevap eksik
                          </span>
                        )}
                        {duplicateIds.has(q.id) && (
                          <span className="pill bg-danger/15 text-danger text-[10px] px-2 py-0.5">
                            mükerrer
                          </span>
                        )}
                        {q.tags.map((t) => (
                          <span
                            key={t}
                            className="pill bg-surfaceHigh text-textMuted text-[10px] px-2 py-0.5"
                          >
                            {t}
                          </span>
                        ))}
                        <span className="text-[10px] text-textMuted sm:hidden">
                          {q.answers.length} cevap
                        </span>
                      </div>
                    </div>

                    <div className="w-24 hidden sm:block">
                      <div className="text-center text-xs font-bold tabular-nums mb-1">
                        <span
                          className={playable ? "text-success" : "text-warning"}
                        >
                          {q.answers.length}
                        </span>
                        <span className="text-textMuted"> / {MIN_ANSWERS}</span>
                      </div>
                      <div className="h-1 rounded-full bg-surfaceHigh overflow-hidden">
                        <div
                          className={`h-full ${
                            playable ? "bg-success" : "bg-warning"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>

                    <div className="w-20 text-center hidden lg:block">
                      {sug > 0 ? (
                        <span className="pill bg-warning/20 text-warning tabular-nums">
                          {sug}
                        </span>
                      ) : (
                        <span className="text-textMuted text-xs">—</span>
                      )}
                    </div>

                    <div className="w-28 text-right hidden lg:block text-[11px] text-textMuted">
                      {relativeTime(q.updatedAtMs)}
                    </div>

                    <div
                      className="w-16 flex justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Toggle
                        checked={q.enabled}
                        // Yayına almak cevap şartına bağlı; kapatmak değil.
                        // Yoksa cevabı sonradan silinmiş bir soru "açık"
                        // durumda kilitli kalıyordu.
                        disabled={busy !== null || (!playable && !q.enabled)}
                        onChange={(v) => toggleEnabled(q, v)}
                        title={
                          playable
                            ? q.enabled
                              ? "Yayından kaldır"
                              : "Yayına al"
                            : `Yayına almak için ${
                                MIN_ANSWERS - q.answers.length
                              } cevap daha gerekiyor`
                        }
                      />
                    </div>
                  </div>
                );
              })}

              {visible.length === 0 && (
                <div className="p-10 text-center text-sm text-textMuted">
                  Bu filtreye uyan soru yok.
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-3 px-1 flex-wrap gap-2">
            <span className="text-[11px] text-textMuted tabular-nums">
              {filtered.length} soru
              {filtered.length !== stats.total && ` (toplam ${stats.total})`}
            </span>
            {pageCount > 1 && (
              <div className="flex items-center gap-2">
                <button
                  className="btn-ghost text-xs"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ‹ Önceki
                </button>
                <span className="text-xs text-textMuted tabular-nums">
                  {page + 1} / {pageCount}
                </span>
                <button
                  className="btn-ghost text-xs"
                  disabled={page >= pageCount - 1}
                  onClick={() =>
                    setPage((p) => Math.min(pageCount - 1, p + 1))
                  }
                >
                  Sonraki ›
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Detay */}
      {current && (
        <QuizDetail
          question={current}
          suggestions={byQuestion.get(current.id) ?? []}
          busy={busy !== null}
          onSave={(patch) => save(current.id, patch)}
          onDelete={() => setConfirmDelete(current)}
          onAcceptSuggestion={acceptSuggestion}
          onDismissSuggestion={dismissSuggestion}
          onClose={() => setOpenId(null)}
        />
      )}

      {/* Yeni soru */}
      <Modal
        open={newOpen}
        title="Yeni soru"
        subtitle={`Cevapları da şimdi girebilirsin. ${MIN_ANSWERS} cevabı olan soru doğrudan yayına girer.`}
        onClose={() => setNewOpen(false)}
        footer={
          <>
            <button className="btn-ghost text-sm" onClick={() => setNewOpen(false)}>
              Vazgeç
            </button>
            <button
              className="btn-primary text-sm"
              disabled={busy !== null || !newText.trim()}
              onClick={createQuestion}
            >
              {busy === "new" ? "Ekleniyor…" : "Ekle"}
            </button>
          </>
        }
      >
        <label className="section-label block mb-2">Soru metni</label>
        <input
          className="input mb-4"
          placeholder="Türkiye'nin komşuları"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          autoFocus
        />
        <label className="section-label block mb-2">
          Cevaplar (isteğe bağlı, her satıra bir tane)
        </label>
        <textarea
          className="input h-40 text-sm font-mono"
          placeholder={"yunanistan\nbulgaristan\ngürcistan\nermenistan\niran"}
          value={newAnswers}
          onChange={(e) => setNewAnswers(e.target.value)}
        />
        <p className="text-[11px] text-textMuted mt-2">
          Eşanlamlıları aynı satırda <code>|</code> ile yaz:{" "}
          <code>amerika birleşik devletleri|abd|amerika</code>
        </p>
      </Modal>

      {/* Toplu ekleme */}
      <Modal
        open={importOpen}
        wide
        title="Toplu soru ekleme"
        subtitle="Yapıştır, önizle, sonra aktar. Bankada zaten olan sorunun üstüne yalnızca eksik cevaplar eklenir."
        onClose={() => setImportOpen(false)}
        footer={
          <>
            <button
              className="btn-ghost text-sm mr-auto"
              disabled={busy !== null}
              onClick={loadSeed}
            >
              {busy === "seed" ? "…" : "Hazır paketi getir"}
            </button>
            <button
              className="btn-ghost text-sm"
              onClick={() => setImportOpen(false)}
            >
              Vazgeç
            </button>
            <button
              className="btn-primary text-sm"
              disabled={busy !== null || importable.length === 0}
              onClick={runImport}
            >
              {busy === "import"
                ? "Aktarılıyor…"
                : merges.length && creates.length
                ? `${creates.length} yeni + ${merges.length} güncelleme`
                : merges.length
                ? `${merges.length} soruyu güncelle`
                : `${creates.length} soruyu aktar`}
            </button>
          </>
        }
      >
        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <label className="section-label block mb-2">Metin</label>
            <textarea
              className="input h-[46vh] text-sm font-mono"
              placeholder={`Türkiye'nin komşuları\nyunanistan\nbulgaristan\ngürcistan\n\nBeyaz olan şeyler\nkar\nsüt\nbulut`}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
            />
            <p className="text-[11px] text-textMuted mt-2 leading-relaxed">
              Bir blok = ilk satır soru, sonraki satırlar cevaplar; blokları
              boş satırla ayır. JSON da kabul ediliyor:{" "}
              <code>{`[{"text":"…","answers":["…"]}]`}</code>
            </p>
          </div>

          <div>
            <label className="section-label block mb-2">
              Önizleme{" "}
              {preview.length > 0 && (
                <span className="text-textSecondary normal-case tracking-normal font-semibold">
                  — {creates.length} yeni, {merges.length} güncellenecek
                  {mergedAnswers > 0 && ` (+${mergedAnswers} cevap)`},{" "}
                  {preview.length - importable.length} atlanacak
                </span>
              )}
            </label>
            <div className="h-[46vh] overflow-y-auto rounded-xl border border-stroke bg-surfaceHigh/40 p-2">
              {preview.length === 0 ? (
                <p className="text-sm text-textMuted p-4 text-center">
                  Soldaki kutuya yapıştır; ne olacağını burada görürsün.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {preview.map((p, i) => {
                    const bad = p.errors.length > 0;
                    // Güncelleme beklenen durum, uyarı değil — sarıya boyamak
                    // 130 satırlık pakette "hepsi sorunlu" izlenimi veriyordu.
                    const tone = bad
                      ? "border-danger/40 bg-danger/5"
                      : p.mode === "skip"
                      ? "border-stroke bg-surfaceHigh/40 opacity-60"
                      : p.mode === "merge"
                      ? "border-primary/40 bg-primary/5"
                      : "border-stroke bg-surface";
                    const icon = bad
                      ? "⛔"
                      : p.mode === "skip"
                      ? "–"
                      : p.mode === "merge"
                      ? "＋"
                      : "✓";
                    return (
                      <div
                        key={i}
                        className={`rounded-lg px-3 py-2 border text-sm ${tone}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-xs mt-0.5">{icon}</span>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold truncate">
                              {p.text || <i className="text-textMuted">(boş)</i>}
                            </div>
                            <div className="text-[11px] text-textMuted">
                              {p.mode === "merge"
                                ? `${p.newAnswers.length} yeni cevap · ${
                                    p.answers.length - p.newAnswers.length
                                  } zaten var`
                                : `${p.answers.length} cevap`}
                              {[...p.errors, ...p.warnings].map((m) => (
                                <span key={m}> · {m}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Etiketleme */}
      <Modal
        open={tagOpen}
        title="Seçilenleri etiketle"
        subtitle={`${selected.size} soruya eklenecek`}
        onClose={() => setTagOpen(false)}
        footer={
          <>
            <button className="btn-ghost text-sm" onClick={() => setTagOpen(false)}>
              Vazgeç
            </button>
            <button
              className="btn-primary text-sm"
              disabled={busy !== null || !tagValue.trim()}
              onClick={bulkTag}
            >
              Etiketle
            </button>
          </>
        }
      >
        <input
          className="input"
          placeholder="ör. coğrafya"
          value={tagValue}
          onChange={(e) => setTagValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && tagValue.trim()) void bulkTag();
          }}
          autoFocus
        />
      </Modal>

      {/* Tekil silme */}
      <Modal
        open={confirmDelete !== null}
        title="Soruyu sil"
        onClose={() => setConfirmDelete(null)}
        footer={
          <>
            <button
              className="btn-ghost text-sm"
              onClick={() => setConfirmDelete(null)}
            >
              Vazgeç
            </button>
            <button
              className="btn-danger text-sm"
              disabled={busy !== null}
              onClick={() => confirmDelete && removeQuestion(confirmDelete)}
            >
              Sil
            </button>
          </>
        }
      >
        <p className="text-sm text-textSecondary">
          <b>{confirmDelete?.text}</b> ve {confirmDelete?.answers.length} cevabı
          kalıcı olarak silinecek.
        </p>
      </Modal>

      {/* Toplu silme */}
      <Modal
        open={confirmBulkDelete}
        title="Seçilenleri sil"
        onClose={() => setConfirmBulkDelete(false)}
        footer={
          <>
            <button
              className="btn-ghost text-sm"
              onClick={() => setConfirmBulkDelete(false)}
            >
              Vazgeç
            </button>
            <button
              className="btn-danger text-sm"
              disabled={busy !== null}
              onClick={bulkDelete}
            >
              {busy === "bulk" ? "Siliniyor…" : `${selected.size} soruyu sil`}
            </button>
          </>
        }
      >
        <p className="text-sm text-textSecondary">
          {selected.size} soru ve tüm cevapları kalıcı olarak silinecek. Bu
          işlem geri alınamaz.
        </p>
      </Modal>

      {toast}
    </div>
  );
}

function Metric({
  value,
  label,
  tone = "neutral",
}: {
  value: string | number;
  label: string;
  tone?: "neutral" | "good" | "warn";
}) {
  const toneClass =
    tone === "warn"
      ? "text-warning"
      : tone === "good"
      ? "text-success"
      : "text-textPrimary";
  return (
    <div className="card p-4">
      <div className={`text-2xl font-extrabold leading-none ${toneClass}`}>
        {value}
      </div>
      <div className="text-[11px] text-textMuted mt-1.5">{label}</div>
    </div>
  );
}
